import { DeduplicationTask, TaskStatus } from '@idea2app/ai-work-order-service';
import { Icon } from 'idea-react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Filter } from 'mobx-restful';
import { Column, Field, RestTable } from 'mobx-restful-table';
import { Component } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-class-tools';

import deduplicationTaskStore from '../model/DeduplicationTask';
import { i18n, I18nKey, t } from '../model/Translation';

const TaskStatusColors: Record<TaskStatus, string> = {
    in_progress: 'primary',
    completed: 'success',
    failed: 'danger'
};

const TaskListName = ({ t }: typeof i18n) => ({
    deduplication: t('deduplication_task_list'),
    sensitive: t('sensitive_task_list'),
    responsible: t('responsible_task_list')
});

@withRouter
@observer
export class TaskListPage extends Component<
    RouteComponentProps<{ type: string }>
> {
    @computed
    get filterFields(): Field<Filter<DeduplicationTask>>[] {
        return [
            { key: 'name', renderLabel: t('task_name') },
            {
                key: 'status',
                type: 'select',
                renderLabel: t('task_status'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'in_progress', label: t('in_progress') },
                    { value: 'completed', label: t('completed') },
                    { value: 'failed', label: t('failed') }
                ]
            }
        ];
    }

    @computed
    get columns(): Column<DeduplicationTask>[] {
        return [
            {
                key: 'id',
                type: 'number',
                renderHead: t('id_number')
            },
            {
                key: 'name',
                renderHead: t('task_name'),
                renderBody: ({ name, id }) => (
                    <a
                        href={`#/task/deduplication/${id}`}
                        className="text-decoration-none"
                    >
                        <Icon name="file-earmark-text" className="me-2" />
                        {name}
                    </a>
                )
            },
            {
                key: 'status',
                renderHead: t('task_status'),
                renderBody: ({ status }) => (
                    <Badge bg={TaskStatusColors[status]}>
                        {t(status as I18nKey)}
                    </Badge>
                )
            },
            {
                key: 'totalWorkOrders',
                type: 'number',
                renderHead: t('order_count'),
                renderBody: ({ totalWorkOrders, processedWorkOrders }) => (
                    <span>
                        {processedWorkOrders || 0} / {totalWorkOrders || 0}
                    </span>
                )
            },
            {
                key: 'startedAt',
                renderHead: t('task_time'),
                renderBody: ({ startedAt, endedAt }) => (
                    <div>
                        <div>
                            {startedAt
                                ? new Date(startedAt).toLocaleString()
                                : '-'}
                        </div>
                        {endedAt && (
                            <div className="text-muted small">
                                {new Date(endedAt).toLocaleString()}
                            </div>
                        )}
                    </div>
                )
            },
            {
                key: 'createdAt',
                renderHead: t('upload_time'),
                renderBody: ({ createdAt }) =>
                    new Date(createdAt).toLocaleString()
            },
            {
                renderHead: t('operation'),
                renderBody: ({ id }) => (
                    <Button
                        variant="link"
                        size="sm"
                        href={`#/task/deduplication/${id}`}
                    >
                        <Icon name="eye" className="me-1" />
                        {t('details')}
                    </Button>
                )
            }
        ];
    }

    render() {
        const { type } = this.props.match.params;
        const typeName = TaskListName(i18n)[type];

        return (
            <Card className="m-3" body>
                <h4>
                    <Icon name="list-task" className="me-2" />
                    {typeName}
                </h4>
                <RestTable
                    filterFields={this.filterFields}
                    columns={this.columns}
                    store={deduplicationTaskStore}
                    translator={i18n}
                    striped
                    hover
                />
            </Card>
        );
    }
}
